import type { ReactNode } from 'react';

import { SiteFooter } from '../components/site-footer';
import { SiteHeader } from '../components/site-header';
import { BRAND_NAME, PAID_PLANS_LAUNCH, SUPPORT_EMAIL } from '../lib/brand';
import { usePageTitle } from '../lib/page-title';

const EFFECTIVE_DATE = 'July 17, 2026';

function LegalPage({ title, children }: LegalPageProps) {
  usePageTitle(title);
  return (
    <>
      <SiteHeader />
      <main className="page legal-page">
        <h1>{title}</h1>
        <p className="legal-effective">Effective {EFFECTIVE_DATE}</p>
        {children}
      </main>
      <SiteFooter />
    </>
  );
}

interface LegalPageProps {
  title: string;
  children: ReactNode;
}

export function PrivacyPage() {
  return (
    <LegalPage title="Privacy policy">
      <section aria-labelledby="privacy-who">
        <h2 id="privacy-who">Who we are</h2>
        <p>
          {BRAND_NAME} (aifinsentiment.com) provides model-derived sentiment analysis of financial
          news. For anything in this policy, contact{' '}
          <a className="text-link" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </section>

      <section aria-labelledby="privacy-data">
        <h2 id="privacy-data">What we collect</h2>
        <ul>
          <li>
            <strong>Account data.</strong> Your email address and a securely hashed password,
            managed by our authentication provider (Supabase). We never see or store your plaintext
            password.
          </li>
          <li>
            <strong>Billing data.</strong> Payments are processed by Stripe. Your card details go
            directly to Stripe and never touch our servers; we store only your subscription status
            and a Stripe customer reference.
          </li>
          <li>
            <strong>Operational logs.</strong> Structured request logs carrying a request ID,
            method, path, status code, and duration. Logs never include request bodies, query
            strings, or credentials, and token-shaped values are masked automatically.
          </li>
          <li>
            <strong>Session storage.</strong> Your sign-in session is kept in your browser's local
            storage so you stay signed in. We do not use advertising or cross-site tracking cookies,
            and we run no third-party analytics.
          </li>
        </ul>
      </section>

      <section aria-labelledby="privacy-use">
        <h2 id="privacy-use">How we use it</h2>
        <p>
          Only to provide and secure the service: authenticating you, deriving your plan from your
          subscription, delivering account emails (confirmation, password reset) through our email
          provider, and diagnosing faults. We do not sell personal data or share it for advertising.
        </p>
      </section>

      <section aria-labelledby="privacy-processors">
        <h2 id="privacy-processors">Processors we rely on</h2>
        <ul>
          <li>Supabase — authentication and database hosting.</li>
          <li>Stripe — subscription billing.</li>
          <li>Hostinger — transactional email delivery.</li>
        </ul>
      </section>

      <section aria-labelledby="privacy-retention">
        <h2 id="privacy-retention">Retention and your rights</h2>
        <p>
          Account data is kept while your account exists. Email{' '}
          <a className="text-link" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>{' '}
          to access, correct, export, or delete your data; deletion removes your account and cancels
          any active subscription. We may retain minimal billing records where required for tax and
          accounting law.
        </p>
      </section>

      <section aria-labelledby="privacy-changes">
        <h2 id="privacy-changes">Changes</h2>
        <p>
          If this policy changes materially, we will note the new effective date here and, for
          significant changes, notify registered users by email.
        </p>
      </section>
    </LegalPage>
  );
}

export function TermsPage() {
  return (
    <LegalPage title="Terms of service">
      <section aria-labelledby="terms-service">
        <h2 id="terms-service">The service</h2>
        <p>
          {BRAND_NAME} shows how a machine-learning model (FinBERT) classifies the tone of licensed
          financial news, aggregated per ticker and per day. Free accounts include a limited set of
          tickers; the Pro subscription unlocks the full catalog as it expands.
        </p>
      </section>

      <section aria-labelledby="terms-advice">
        <h2 id="terms-advice">Not investment advice</h2>
        <p>
          Everything in this service is informational, automated analysis of news text. It is not
          financial, investment, legal, or tax advice; it is not a recommendation to buy or sell any
          security; and it may be incomplete, delayed, or wrong. Model outputs are probabilistic
          classifications of language, not statements about companies or markets. You are solely
          responsible for your investment decisions.
        </p>
      </section>

      <section aria-labelledby="terms-accounts">
        <h2 id="terms-accounts">Accounts</h2>
        <p>
          Keep your credentials confidential; you are responsible for activity under your account.
          We may suspend accounts that abuse the service, attempt to bypass plan boundaries or rate
          limits, scrape content, or interfere with its operation.
        </p>
      </section>

      <section aria-labelledby="terms-billing">
        <h2 id="terms-billing">Subscriptions and billing</h2>
        <p>
          The service is currently in open beta and billing is disabled. Paid Pro subscriptions
          launch on {PAID_PLANS_LAUNCH}. Pro is a recurring subscription billed through Stripe. You
          can cancel at any time from your account's billing portal; cancellation takes effect at
          the end of the paid period, and your plan keeps its Pro access until then. Prices may
          change with notice before your next renewal.
        </p>
      </section>

      <section aria-labelledby="terms-content">
        <h2 id="terms-content">Content and licenses</h2>
        <p>
          Headlines and article summaries remain the property of their original copyright holders.
          Sentiment scores and aggregates are generated by the service for your personal,
          non-commercial use within the product. Automated bulk extraction or redistribution of
          either is not permitted.
        </p>
      </section>

      <section aria-labelledby="terms-warranty">
        <h2 id="terms-warranty">Warranty and liability</h2>
        <p>
          The service is provided “as is” without warranties of accuracy, availability, or fitness
          for a particular purpose. To the maximum extent permitted by law, our total liability for
          any claim related to the service is limited to the amount you paid us in the twelve months
          before the claim, and we are not liable for indirect or consequential losses, including
          trading losses.
        </p>
      </section>

      <section aria-labelledby="terms-changes">
        <h2 id="terms-changes">Changes and contact</h2>
        <p>
          We may update these terms; material changes will carry a new effective date and, where
          significant, email notice. Continued use after a change means acceptance. Questions go to{' '}
          <a className="text-link" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </section>
    </LegalPage>
  );
}
